import {
  supplierStaffQuery,
  useDeleteTeamMemberMutation,
  useResendSupplierStaffInvitationMutation,
} from "@/api/supplier";
import { DataTable } from "@/components/data-table/DataTable";
import { ColumnDef, DataTableBody } from "@/components/data-table/DataTableBody";
import { Beta } from "@/components/headless/Beta";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogClose } from "@/components/ui/Dialog";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { copyToClipboard } from "@/utils/clipboard";
import { getErrorMessage } from "@/utils/error";
import { seo } from "@/utils/seo";
import { Box, Flex, QueryHandler, toast } from "@givenwell/components";
import { CopyIcon, DotsIcon, RecycleBinIcon, ReloadIcon } from "@givenwell/core-solid-icons";
import { SupplierStaffUser } from "@givenwell/management-api";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { InviteTeamMemberModal } from "./InviteTeamMember";

export const Route = createFileRoute("/supplier/$supplierId/settings/team/")({
  component: TeamTableLoader,
  head: () => ({
    meta: seo({ title: "Team" }),
  }),
});

function TeamTableLoader() {
  const { supplierId } = Route.useParams();
  const query = useQuery(supplierStaffQuery(supplierId));

  return (
    <PageWrapper
      title="Your Team"
      subTitle={<>Add and manage team members.</>}
      maxWidth={1400}
      rightContent={<InviteTeamMemberModal />}
    >
      <QueryHandler query={query} success={({ data: staff }) => <TeamTable supplierId={supplierId} staff={staff} />} />
    </PageWrapper>
  );
}

type LocationsTableProps = { supplierId: string; staff: SupplierStaffUser[] };
function TeamTable({ supplierId, staff }: LocationsTableProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetTeamMember, setTargetTeamMember] = useState({} as SupplierStaffUser);
  const deleteTeamMemberMut = useDeleteTeamMemberMutation();
  const resendInviteMut = useResendSupplierStaffInvitationMutation();

  function handleDeleteClick(userId: string) {
    const teamMember = staff.find(staff => staff.user_id === userId);
    if (!teamMember) return;

    setTargetTeamMember(teamMember);

    setTimeout(() => {
      setDeleteModalOpen(true);
    }, 0);
  }

  async function handleResendClick(invitationId?: string) {
    if (!invitationId) return;

    try {
      await resendInviteMut.mutateAsync({ supplierId, invitationId });
      toast.success("Invitation resent");
    } catch (e) {
      const reason = getErrorMessage(e);
      toast.error("Error", {
        description: `Failed to resend invitation: ${reason}`,
      });
    }
  }

  async function handelDeleteTeamMemberClick() {
    await deleteTeamMemberMut.mutateAsync({ supplierId: supplierId, userId: targetTeamMember.user_id });
    setDeleteModalOpen(false);
    toast.success("Team member removed");
  }

  const staffColumns: ColumnDef<SupplierStaffUser>[] = [
    {
      header: "First Name",
      cell: "given_name",
    },
    {
      header: "Last Name",
      cell: "family_name",
    },
    {
      header: "Email",
      cell: m => (
        <Flex css={{ alignItems: "center" }}>
          {m.email}{" "}
          <IconButton
            title="Copy to clipboard"
            size="sm"
            aria-label="Copy to clipboard"
            variant="plain"
            css={{ padding: 0, m: 0 }}
            onClick={() => copyToClipboard(m.email)}
          >
            <CopyIcon width="14" height="14" />
          </IconButton>
        </Flex>
      ),
    },
    {
      header: "Invitation Status",
      cell: s => (s.invitation_details?.status ? `Invitation ${s.invitation_details?.status}` : ""),
    },
    {
      header: "",
      css: { w: 0 },
      cell: staff => (
        <DropdownMenu
          content={
            <>
              {staff.invitation_details?.status !== "accepted" && (
                <DropdownMenuItem
                  icon={<ReloadIcon />}
                  onClick={() => handleResendClick(staff.invitation_details?.invitation_id)}
                >
                  Resend invitation
                </DropdownMenuItem>
              )}
              <DropdownMenuItem color="red" icon={<RecycleBinIcon />} onClick={() => handleDeleteClick(staff.user_id)}>
                Remove team member
              </DropdownMenuItem>
              <Beta>
                <DropdownMenuItem icon={<CopyIcon />} onClick={() => copyToClipboard(staff.user_id)}>
                  Copy user ID
                </DropdownMenuItem>
              </Beta>
            </>
          }
        >
          <IconButton variant="plain" color="neutral" aria-label="Actions" size="sm">
            <DotsIcon />
          </IconButton>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <Box>
      <DataTable>
        <DataTableBody
          emptyStateMessage="No team members. Unless you're a Givenwell admin, you really shouldn't be seeing this!!"
          columns={staffColumns}
          data={staff}
          dataKey="user_id"
          rounded="full"
        />
      </DataTable>

      <Dialog
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        title="Remove Team Member"
        content={
          <>
            <Alert status="warning" css={{ mb: 16 }}>
              Warning – this action is permanent and cannot be reversed.
            </Alert>
            <Dl>
              <DlRow>
                <Dt>First name</Dt>
                <Dd>{targetTeamMember.given_name}</Dd>
              </DlRow>
              <DlRow>
                <Dt>Last name</Dt>
                <Dd>{targetTeamMember.family_name}</Dd>
              </DlRow>
              <DlRow>
                <Dt>Email</Dt>
                <Dd>{targetTeamMember.email}</Dd>
              </DlRow>
            </Dl>
          </>
        }
        footer={
          <>
            <DialogClose>
              <Button variant="plain" color="neutral" css={{ flex: 1 }}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="filled"
              color="red"
              css={{ flex: 1 }}
              loading={deleteTeamMemberMut.isPending}
              onClick={handelDeleteTeamMemberClick}
            >
              Delete
            </Button>
          </>
        }
      />
    </Box>
  );
}

const Dl = styled("dl", {
  borderTop: `1px solid ${colors.outline}`,
});
const DlRow = styled("div", {
  borderBottom: `1px solid ${colors.outline}`,
  py: 16,
});
const Dt = styled("dt", {
  fontScale: "xs",
});
const Dd = styled("dd", {
  weight: 500,
});
